/*
 * Portfolio Sign-Up (Issue 3) — dedicated first-time account creation for
 * authorizers / EMOs / CMOs / districts / networks / portfolio managers.
 * Creates a Portfolio Owner account (no school tenant), then hands off to login.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/PhoneField";
import { toast } from "@/components/ui/sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];
const fieldClass = "h-11 bg-white border border-[#e5e7eb] text-[#111827]";

export default function PortfolioSignup() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ organization_types: [], purchase_methods: [], sales_contacts: [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [existingAccount, setExistingAccount] = useState(null);
  const [f, setF] = useState(() => {
    const base = {
      first_name: "", last_name: "", email: "", password: "", confirm_password: "", phone: "", title: "",
      organization_name: "", organization_type: "", state: "",
      sales_contact_id: "", sales_channel: "Direct", purchase_method: "Not Sure",
    };
    try {
      const pre = JSON.parse(sessionStorage.getItem("lead_prefill") || "null");
      if (pre) return { ...base,
        first_name: pre.first_name || "", last_name: pre.last_name || "",
        email: pre.email || "", phone: pre.phone || "", title: pre.title || "",
        organization_name: pre.organization_name || "", state: pre.state || "",
        sales_channel: pre.sales_contact_name ? "Referral" : "Direct" };
    } catch { /* ignore */ }
    return base;
  });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { axios.get(`${API}/api/portfolio-signup/meta`).then((r) => setMeta(r.data)).catch(() => {}); }, []);

  // Prefill from a tracked lead link (attribution-preserving). Read the `lt`
  // token from the URL and fetch fresh prefill from the backend (robust across
  // refresh); fall back to sessionStorage set by the /l/:token handler.
  useEffect(() => {
    const applyPrefill = (pre) => {
      if (!pre) return;
      setF((prev) => ({ ...prev,
        first_name: pre.first_name || prev.first_name, last_name: pre.last_name || prev.last_name,
        email: pre.email || prev.email, phone: pre.phone || prev.phone,
        title: pre.title || prev.title, organization_name: pre.organization_name || prev.organization_name,
        state: pre.state || prev.state,
        sales_channel: pre.sales_contact_name ? "Referral" : prev.sales_channel }));
    };
    const params = new URLSearchParams(window.location.search);
    const lt = params.get("lt") || sessionStorage.getItem("lead_token");
    if (lt) {
      axios.get(`${API}/api/lead-link/${lt}`).then((r) => applyPrefill(r.data?.prefill)).catch(() => {});
    } else {
      try { applyPrefill(JSON.parse(sessionStorage.getItem("lead_prefill") || "null")); } catch { /* ignore */ }
    }
  }, []);

  const submit = async () => {
    for (const k of ["first_name", "last_name", "email", "organization_name", "organization_type", "state"]) {
      if (!f[k]) { toast.error("Please complete all required fields"); return; }
    }
    if ((f.password || "").length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (f.password !== f.confirm_password) { toast.error("Passwords do not match"); return; }
    if (!f.sales_contact_id) { toast.error("Please select a Sales Contact (or Direct Inquiry)"); return; }
    setSubmitting(true);
    try {
      const contact = meta.sales_contacts.find((c) => c.id === f.sales_contact_id);
      const payload = { ...f, sales_contact_name: contact?.name || (f.sales_contact_id === "direct" ? "Direct Inquiry" : "") };
      const r = await axios.post(`${API}/api/portfolio-signup`, payload);
      if (r.data && r.data.ok === false && r.data.code === "account_exists") {
        // Existing account — stash the form so we can finish after sign-in, then
        // show a friendly panel instead of a duplicate-account error.
        const safe = { ...payload };
        delete safe.password; delete safe.confirm_password;
        sessionStorage.setItem("pf_resume_form", JSON.stringify(safe));
        setExistingAccount({ email: f.email, message: r.data.message });
        return;
      }
      const lt = sessionStorage.getItem("lead_token");
      if (lt) { axios.post(`${API}/api/lead-link/${lt}/converted`, { email: f.email }).catch(() => {}); sessionStorage.removeItem("lead_token"); }
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create your account.");
    } finally { setSubmitting(false); }
  };

  // Resume path: an existing user who signed in returns here to finish setup on
  // their own account via /attach — no second account, no error.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("resume") !== "1") return;
    (async () => {
      try {
        const me = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        if (!me?.data) return;
        const saved = JSON.parse(sessionStorage.getItem("pf_resume_form") || "null");
        if (!saved) return;
        const r = await axios.post(`${API}/api/portfolio-signup/attach`, saved, { withCredentials: true });
        const lt = sessionStorage.getItem("lead_token");
        if (lt) { axios.post(`${API}/api/lead-link/${lt}/converted`, { email: saved.email }).catch(() => {}); sessionStorage.removeItem("lead_token"); }
        sessionStorage.removeItem("pf_resume_form");
        setResult(r.data);
      } catch { /* not signed in yet or nothing to resume */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="portfolio-signup-page">
      <header className="sticky top-0 z-30 bg-white border-b border-[#e5e7eb]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex items-center justify-between h-20">
          <button onClick={() => navigate("/myschoolrx")} className="flex items-center"><img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto" /></button>
          <a href="/diagnostic/login" className="text-[13px] font-semibold text-[#374151]">Sign in</a>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
        {result ? (
          <div className="bg-white rounded-2xl border border-[#e5e7eb] p-8 text-center" data-testid="portfolio-signup-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-[#111827]">Your Portfolio account is ready</h1>
            <p className="mt-2 text-[14px] text-[#4b5563]">{result.linked ? <>Your portfolio is set up on your existing account.</> : <>We've created your account for <strong>{f.organization_name}</strong>. Sign in with the email and password you just created to select your schools and invite each school's contact.</>}</p>
            <p className="mt-3 text-[12px] text-[#6b7280]">Login email: {result.email}</p>
            <Button onClick={() => navigate(result.linked ? "/portfolio/dashboard" : "/diagnostic/login")} className="mt-6 bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="portfolio-goto-login">{result.linked ? "Go to Your Portfolio" : "Sign in & Create Portfolio"} <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </div>
        ) : existingAccount ? (
          <div className="bg-white rounded-2xl border border-[#e5e7eb] p-8 text-center" data-testid="portfolio-signup-existing">
            <CheckCircle2 className="h-12 w-12 text-[#007a9e] mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-[#111827]">You already have an account</h1>
            <p className="mt-2 text-[14px] text-[#4b5563]">Good news — <strong>{existingAccount.email}</strong> already has a MySchoolRx account. Sign in and we'll finish setting up your portfolio on that account. Nothing to re-create.</p>
            <Button onClick={() => navigate(`/diagnostic/login?next=${encodeURIComponent("/signup/portfolio?resume=1")}`)} className="mt-6 bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="portfolio-existing-signin">Sign in to continue <ArrowRight className="h-4 w-4 ml-2" /></Button>
            <button onClick={() => setExistingAccount(null)} className="mt-4 block mx-auto text-[12px] text-[#6b7280] underline">Use a different email</button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#e5e7eb] p-6 sm:p-8">
            <h1 className="text-2xl font-bold text-[#111827]">Create Your Portfolio Account</h1>
            <p className="mt-2 text-[14px] text-[#6b7280]">Set up access for an authorizer, EMO, CMO, district, school network, or portfolio manager responsible for multiple schools.</p>

            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-[12px] font-semibold text-[#374151] mb-1.5">First name *</label><Input className={fieldClass} value={f.first_name} onChange={up("first_name")} data-testid="pf-first" /></div>
                <div><label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Last name *</label><Input className={fieldClass} value={f.last_name} onChange={up("last_name")} data-testid="pf-last" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Email *</label><Input type="email" className={fieldClass} value={f.email} onChange={up("email")} data-testid="pf-email" /></div>
                <div><label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Phone</label><PhoneField value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="pf-phone" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Create password *</label><Input type="password" className={fieldClass} value={f.password} onChange={up("password")} placeholder="At least 8 characters" data-testid="pf-password" /></div>
                <div><label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Confirm password *</label><Input type="password" className={fieldClass} value={f.confirm_password} onChange={up("confirm_password")} data-testid="pf-password-confirm" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Title / Role</label><Input className={fieldClass} value={f.title} onChange={up("title")} data-testid="pf-title" /></div>
                <div><label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Organization name *</label><Input className={fieldClass} value={f.organization_name} onChange={up("organization_name")} data-testid="pf-org" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Organization type *</label>
                  <select className="h-11 w-full bg-white border border-[#e5e7eb] rounded-md text-[14px] px-3" value={f.organization_type} onChange={up("organization_type")} data-testid="pf-org-type">
                    <option value="">Select…</option>
                    {meta.organization_types.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">State *</label>
                  <select className="h-11 w-full bg-white border border-[#e5e7eb] rounded-md text-[14px] px-3" value={f.state} onChange={up("state")} data-testid="pf-state">
                    <option value="">Select…</option>
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Sales Contact *</label>
                <select className="h-11 w-full bg-white border border-[#e5e7eb] rounded-md text-[14px] px-3" value={f.sales_contact_id} onChange={up("sales_contact_id")} data-testid="pf-sales-contact">
                  <option value="">Select…</option>
                  <option value="direct">Direct Sale / No Sales Contact</option>
                  {meta.sales_contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.title ? ` — ${c.title}` : ""}</option>)}
                </select>
                <p className="text-[11px] text-[#6b7280] mt-1">Select the 589 Labs contact who introduced you to the platform.</p>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Purchase Method</label>
                <select className="h-11 w-full bg-white border border-[#e5e7eb] rounded-md text-[14px] px-3" value={f.purchase_method} onChange={up("purchase_method")} data-testid="pf-purchase-method">
                  {meta.purchase_methods.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button disabled={submitting} onClick={submit} className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50" data-testid="pf-submit">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : "Create Portfolio Account"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
